import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface ChartData {
  [questionId: number]: string; // Base64 image string
}

interface Step {
  title: string;
  content: string;
}

interface StepData {
  [questionId: number]: Step[];
}

interface QuestionHistory {
  exercise_id: number;
  current: number[];
  completed: number[];
  in_process: number[];
}
interface QuestionsState {
  currentSelectedQuestion: string | null;
  currentSelectedStep: number | null;
  currentQuestions: number[];
  completedQuestions: number[];
  charts: ChartData;
  steps: StepData;
  selectedQuestionLetter: string | null;

  question_history: QuestionHistory | null;

  setCurrentSelectedQuestion: (questionId: number | null) => void;
  setCurrentSelectedStep: (stepIndex: number | null) => void;   // ✅ New
  clearCurrentSelectedStep: () => void;  

  // Current Questions
  addCurrentQuestions: (questionId: number | number[]) => void;
  getCurrentQuestions: () => number[];
  clearCurrentQuestions: () => void;

  // Completed Questions
  addCompletedQuestion: (exerciseId: number, questionId: number) => void;
  removeCompletedQuestion: (questionId: number) => void;
  getCompletedQuestions: () => number[];
  clearCompletedQuestions: () => void;

  markQuestionInProcess: (exerciseId: number, questionId: number) => void;

  // Chart
  setChart: (data: { question_id: number; chart_image_base64: string }) => void;
  getChartByQuestionId: (questionId: number) => string | undefined;
  clearCharts: () => void;

  // Steps
  setSteps: (questionId: number, steps: Step[]) => void;
  getStepsByQuestionId: (questionId: number) => Step[] | undefined;
  clearSteps: () => void;

  setSelectedQuestionLetter: (question: string) => void;
  clearSelectedQuestionLetter: () => void;

  setQuestionHistory: (history: QuestionHistory) => void;
  getQuestionHistory: (exerciseId: number) => QuestionHistory | null;
  clearQuestionHistory: () => void;

  extractedQuestions: string[] | null;
  setExtractedQuestions: (questions: string[] | null) => void;
  getExtractedQuestions: () => string[] | null;
  clearExtractedQuestions: () => void;
}

export const useQuestionsStore = create(
  persist<QuestionsState>(
    (set, get) => ({
      currentSelectedQuestion: null,
      currentSelectedStep: null,
      currentQuestions: [],
      completedQuestions: [],
      charts: {},
      steps: {},
      selectedQuestionLetter: null,
      extractedQuestions: null,

      question_history: null,

      setCurrentSelectedQuestion: (questionId: any) => set({ currentSelectedQuestion: questionId }),

      setCurrentSelectedStep: (stepIndex) => set({ currentSelectedStep: stepIndex }),
      clearCurrentSelectedStep: () => set({ currentSelectedStep: null }),  

      // Extracted Questions
      setExtractedQuestions: (questions) => set({ extractedQuestions: questions }),
      getExtractedQuestions: () => get().extractedQuestions,
      clearExtractedQuestions: () => set({ extractedQuestions: null }),  

      // Current Questions
      addCurrentQuestions: (questionId) => {
        const current = get().currentQuestions;
        let newIds: number[] = [];

        if (Array.isArray(questionId)) {
          newIds = [...new Set(questionId)].filter((id) => !current.includes(id));
        } else {
          if (!current.includes(questionId)) newIds = [questionId];
        }

        if (newIds.length > 0) {
          set({ currentQuestions: [...current, ...newIds] });
        }
      },
      getCurrentQuestions: () => get().currentQuestions,
      clearCurrentQuestions: () => set({ currentQuestions: [] }),

      // Completed Questions
      addCompletedQuestion: (exerciseId, questionId) => {
        const completed = get().completedQuestions;
        const history = get().question_history;

        // Root-level tracking
        if (!completed.includes(questionId)) {
          set({ completedQuestions: [...completed, questionId] });
        }

        // History-level tracking
        if (history && history.exercise_id === exerciseId) {
          const updatedHistory: QuestionHistory = {
            ...history,
            completed: history.completed.includes(questionId)
              ? history.completed
              : [...history.completed, questionId],
            in_process: history.in_process.filter((id) => id !== questionId),
          };
          set({ question_history: updatedHistory });
        }
      },
      removeCompletedQuestion: (questionId) => {
        set({
          completedQuestions: get().completedQuestions.filter((id) => id !== questionId),
        });
      },
      getCompletedQuestions: () => get().completedQuestions,
      clearCompletedQuestions: () => set({ completedQuestions: [] }),

      markQuestionInProcess: (exerciseId, questionId) => {
        const history = get().question_history;

        if (history && history.exercise_id === exerciseId) {
          const updatedHistory: QuestionHistory = {
            ...history,
            current: history.current.includes(questionId)
              ? history.current
              : [...history.current, questionId],
            in_process: history.in_process.includes(questionId)
              ? history.in_process
              : [...history.in_process, questionId],
          };
          set({ question_history: updatedHistory });
        } else {
          // If no history yet, create it
          set({
            question_history: {
              exercise_id: exerciseId,
              current: [questionId],
              completed: [],
              in_process: [questionId],
            },
          });
        }
      },

      // Chart
      setChart: ({ question_id, chart_image_base64 }) => {
        const chartUrl = `data:image/png;base64,${chart_image_base64}`;
        set((state) => ({
          charts: {
            ...state.charts,
            [question_id]: chartUrl,
          },
        }));
      },
      getChartByQuestionId: (questionId) => get().charts[questionId],
      clearCharts: () => set({ charts: {} }),

      // Steps
      setSteps: (questionId, steps) => {
        set((state) => ({
          steps: {
            ...state.steps,
            [questionId]: steps,
          },
        }));
      },
      getStepsByQuestionId: (questionId?: number) => {
        if (typeof questionId !== 'number') return [];
        return get().steps[questionId] || [];
      },
      clearSteps: () => set({ steps: {} }),

      setSelectedQuestionLetter: (questionLetter) => set({ selectedQuestionLetter: questionLetter }),
      clearSelectedQuestionLetter: () => set({ selectedQuestionLetter: null }),

      setQuestionHistory: (history) =>
        set({
          question_history: {
            exercise_id: history.exercise_id,
            current: history.current || [],
            completed: history.completed || [],
            in_process: history.in_process || [],
          },
        }),

      getQuestionHistory: (exerciseId) => {
        const state = get();
        const history = state.question_history;
        if (!history || history.exercise_id !== exerciseId) {
          return null;
        }
        return history;
      },
      clearQuestionHistory: () => set({ question_history: null }),
    }),
    {
      name: "shoonit-questions",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
